// PouchDB adapter backed by CsSQLitePlugin (iOS native only)
// Implements the ~12 _-prefixed methods that PouchDB expects from an adapter.
//
// CRITICAL: adapter methods must NOT be `async` functions. PouchDB's `toPromise`
// wrapper checks if the return value is a thenable — async functions always return
// a promise, causing toPromise to resolve immediately with undefined instead of
// waiting for the callback. Use void async IIFEs inside regular functions.

import { CsSQLite, type CsSQLitePlugin, type ExecuteResult } from 'services/cs-sqlite-plugin';
import {
  winningRev, isDeleted, isLocalId, traverseRevTree,
  collectConflicts, collectLeaves, compactTree,
  merge, revExists, latest, parseDoc
} from './utils';
import { withSqliteOpenTimeout, SQLITE_OPEN_TIMEOUT_MS } from './sqlite-open-timeout';
import { bootReadPriorityGate } from './boot-read-priority';
import type { LocalAdapterFaultReporter, LocalCorruptionReporter } from './local-corruption-reporter';
import { describeError } from '@cf-shared/error-describe';

// ─── SQL Schema ───────────────────────────────────────────────────────

const SCHEMA_SQL = [
  `CREATE TABLE IF NOT EXISTS doc_store (
    id TEXT PRIMARY KEY,
    json TEXT,
    winningRev TEXT,
    deleted INTEGER DEFAULT 0,
    seq INTEGER DEFAULT 0
  )`,
  `CREATE TABLE IF NOT EXISTS by_seq_store (
    seq INTEGER PRIMARY KEY AUTOINCREMENT,
    doc_id_rev TEXT UNIQUE,
    json TEXT
  )`,
  `CREATE TABLE IF NOT EXISTS local_store (
    id TEXT PRIMARY KEY,
    rev TEXT,
    json TEXT
  )`,
  `CREATE TABLE IF NOT EXISTS meta_store (
    id TEXT PRIMARY KEY,
    value TEXT
  )`,
  `CREATE TABLE IF NOT EXISTS attach_and_seq_store (
    seq INTEGER,
    digest_seq TEXT UNIQUE
  )`,
  // #4343: the changes feed joins by_seq_store to doc_store on seq. Without
  // this index SQLite would scan doc_store once per scanned by_seq row —
  // and the feed is the hot path every full view rebuild walks. IF NOT
  // EXISTS makes it apply to existing on-device databases at open time.
  `CREATE INDEX IF NOT EXISTS doc_store_seq_idx ON doc_store (seq)`
];

// ─── Null-byte encoding ───────────────────────────────────────────────
// SQLite TEXT columns truncate at \u0000 (C string convention).
// PouchDB-find index document IDs contain \u0000 as delimiters.
// We escape null bytes before storing and unescape after reading.
//   \u0001 → \u0001\u0002   (escape the escape char first)
//   \u0000 → \u0001\u0001   (then encode null)

//
// Exported ONLY so sql-escape.test.ts can check the round trip directly. It is
// not called from outside this file, and should not be. Testing this through
// the public adapter API instead would prove nothing: the Node test backend
// (node:sqlite) does not truncate at a NUL the way SQLite's C API does, so a
// document round trip would still pass with the escaping deleted. The escape
// scheme is a pure string property, so it is checked as one.
/* eslint-disable no-control-regex */
export function sqlEncode(s: string): string {
  if (s.indexOf('\u0000') === -1 && s.indexOf('\u0001') === -1) return s;
  return s.replace(/\u0001/g, '\u0001\u0002').replace(/\u0000/g, '\u0001\u0001');
}

export function sqlDecode(s: string): string {
  if (s.indexOf('\u0001') === -1) return s;
  return s.replace(/\u0001\u0001/g, '\u0000').replace(/\u0001\u0002/g, '\u0001');
}
/* eslint-enable no-control-regex */

// ─── Helpers ──────────────────────────────────────────────────────────

function encodeMetadata(metadata: any, winRev: string, deleted: boolean): { id: string, json: string, winningRev: string, deleted: number, seq: number } {
  return {
    id: sqlEncode(metadata.id),
    json: JSON.stringify(metadata),
    winningRev: winRev,
    deleted: deleted ? 1 : 0,
    seq: metadata.seq || 0
  };
}

function decodeMetadata(row: any): any {
  if (!row) return null;
  const metadata = JSON.parse(row.json);
  metadata.winningRev = row.winningRev;
  metadata.deleted = row.deleted === 1;
  metadata.seq = row.seq;
  return metadata;
}

function decodeDoc(row: any): any {
  if (!row) return null;
  const doc = JSON.parse(row.json);
  const docIdRev: string = sqlDecode(row.doc_id_rev);
  const idx = docIdRev.lastIndexOf(':');
  doc._id = docIdRev.substring(0, idx - 1);
  doc._rev = docIdRev.substring(idx + 1);
  return doc;
}

function pouchError(status: number, name: string, message: string): any {
  const err: any = new Error(message);
  err.status = status;
  err.name = name;
  err.message = message;
  err.error = true;
  return err;
}

const MISSING_DOC = () => pouchError(404, 'not_found', 'missing');
const REV_CONFLICT = () => pouchError(409, 'conflict', 'Document update conflict');

/**
 * How many rows one filtered pass over the changes feed reads at a time (#4798).
 *
 * Only a FILTERED read needs a window: with no filter every scanned row is reported, so
 * the caller's `limit` bounds the query directly. With one, the query is asked for this
 * many rows, the filter is applied to them, and the feed is walked again from the last
 * row scanned until enough rows have passed the filter or a window comes back short.
 *
 * The size trades two costs against each other, and both are real. Too small and a feed
 * whose rows are mostly filtered out makes many round trips across the Capacitor bridge;
 * too large and a read that needed a handful of rows drags whole document bodies over it.
 * A window at least as large as the caller's own `limit` means the common case — a filter
 * that keeps most rows — still finishes in one query.
 */
const CHANGES_FILTERED_SCAN_WINDOW = 500;

/**
 * Decide whether one change may be reported, and strip the body the filter needed.
 *
 * Copied from PouchDB's own `filterChange` (pouchdb-utils) so this adapter behaves
 * like the built-in IndexedDB one. It is copied rather than imported because
 * `pouchdb-utils` is not a direct dependency of the client — the same reason the
 * revision-tree helpers in `utils.ts` are copies.
 *
 * #4798: a caller can exclude documents from the changes feed in two ways, and both
 * arrive here as `opts.filter` being a function — PouchDB's core turns a Mango
 * `selector` into one before it ever reaches an adapter. This adapter used to ignore
 * `opts.filter` entirely, so the initial sync's push half (whose source is the LOCAL
 * database, and whose only exclusion is a selector) pushed device-local documents to
 * the server on every iOS boot.
 *
 * Two details are load-bearing and easy to lose:
 *  - the filter is given a real body even when `include_docs` is false, because a
 *    selector filters on fields; the body is deleted again afterwards, so a caller
 *    that did not ask for bodies still does not get them;
 *  - a filter that THROWS returns the error object rather than a boolean, and the
 *    caller must fail the whole feed on it instead of treating it as "excluded".
 *
 * Returns `true` to report the change, `false` to drop it, or an Error to fail the feed.
 */
function filterChange(opts: any): (change: any) => boolean | Error {
  const req: any = { query: opts.query_params };
  const hasFilter = opts.filter && typeof opts.filter === 'function';

  return function filter(change: any): boolean | any {
    if (!change.doc) {
      // PouchDB's own comment: some changes feeds send events with no document,
      // and giving the filter an empty object keeps every filter robust.
      change.doc = {};
    }

    if (hasFilter) {
      let excluded: boolean;
      try {
        excluded = !opts.filter(change.doc, req);
      } catch (err: any) {
        return pouchError(400, 'bad_request', 'Filter function threw: ' + String(err));
      }
      if (excluded) return false;
    }

    if (!opts.include_docs) {
      delete change.doc;
    }
    return true;
  };
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// ─── Write priority gate ──────────────────────────────────────────────
// Ensures small user saves (1-5 docs) execute quickly even when background
// sync is writing large batches. Sync chunks yield and wait for any pending
// user writes to complete before continuing.

class WritePriorityGate {
  private userWritesPending = 0;
  private resolvers: (() => void)[] = [];

  /** Called by user writes (small batches). Returns immediately. */
  enterUserWrite() { this.userWritesPending++; }
  exitUserWrite() {
    this.userWritesPending--;
    if (this.userWritesPending === 0) {
      const fns = this.resolvers.splice(0);
      for (const fn of fns) fn();
    }
  }

  /** Called by sync between chunks. Waits if user writes are pending. */
  async waitForUserWrites(): Promise<void> {
    if (this.userWritesPending === 0) return;
    return new Promise(resolve => this.resolvers.push(resolve));
  }
}

/**
 * Runs write units one at a time, in the order they were requested (#4417).
 *
 * WHY THIS EXISTS. A `_bulkDocs` unit of work has to READ each document's
 * current revision list out of `doc_store`, merge the incoming revisions into
 * it, and WRITE the result back. Those are two separate calls into the native
 * plugin with JavaScript awaits in between, so without this serializer two
 * callers can interleave: both read the same revision list, then both write.
 * The second write lands a revision list computed before the first write
 * existed, so the first caller's revision silently disappears from it.
 *
 * That alone would only lose an update. It becomes unrecoverable because
 * auto-compaction is on: the first caller, on its way out, deletes the body of
 * the revision its own write superseded. In the revision list that survives,
 * that same revision is the newest one again — so it wins, and its body is
 * gone. From then on `_get` reports the document missing while `_bulkDocs`
 * refuses every write as a conflict, and every save is dropped. That is the
 * #4417 workout-data loss.
 *
 * The upstream IndexedDB and WebSQL adapters never had this problem because
 * they read `doc_store` INSIDE the same database transaction that writes it.
 * This adapter cannot: each native call is its own transaction. So the
 * exclusion has to live here, on the JavaScript side.
 *
 * Scope note: a unit is one CHUNK, not one whole `_bulkDocs` call. Sync writes
 * deliberately yield to user saves between chunks (see the chunked path in
 * `_bulkDocs`), and holding this across a whole multi-chunk batch would undo
 * that and make user saves wait behind a long sync write.
 *
 * Also routed through here: `_doCompaction` is the same read-modify-write of
 * `doc_store` across separate native calls, so it now takes this serializer too
 * (see `api._doCompaction`). It has no caller in `client/src` today — PouchDB
 * reaches it from `db.compact()` and `db.compactDocument()`, and nothing calls
 * either — so the interleave could not happen yet; serializing it keeps a future
 * `compact()` call safe by construction rather than relying on that staying true.
 *
 * WHAT THIS DOES NOT COVER, so nobody reads more into it than it delivers:
 * - Two entries for the SAME document id inside ONE unit. That is the other
 *   half of the same hazard and this serializer does nothing about it, but it
 *   is no longer unhandled: #4431 made the merge pass refresh its snapshot per
 *   row (`registerForLaterRowsInThisUnit`), so the second entry now builds on
 *   the first instead of overwriting it. Neither fix covers the other's case.
 *   See design-docs/sync-integrity.md, "What #4431 measured".
 * - READS. A read never takes this serializer, so a read split across two native
 *   calls can see a save land in between. Any read that needs a document's
 *   metadata AND a body named by it (the winner's, say) must take both in ONE
 *   statement, as `_get`, `_allDocs` and the changes feed do since #6351. Two
 *   calls turned healthy documents into false "winning body missing" reports,
 *   false 404s and empty `{}` bodies.
 */
export class WriteSerializer {
  private tail: Promise<unknown> = Promise.resolve();

  /** Queue `work` behind everything already queued. The returned promise
   *  settles with `work`'s own result; a failure in one unit never stops the
   *  next one from running. */
  runExclusiveAsync<T>(work: () => Promise<T>): Promise<T> {
    // `tail` never rejects — the line below always swallows — so `then` needs
    // no rejection arm here. That swallow is what delivers the isolation:
    // without it, one failed unit would reject every unit queued after it.
    const result = this.tail.then(work);
    this.tail = result.then(() => undefined, () => undefined);
    return result;
  }
}

// ─── `_local/...` document writes ─────────────────────────────────────
//
// #4717: every call into the native plugin is one round trip across the
// Capacitor bridge, and there is ONE bridge shared by everything, so the
// NUMBER of calls is what costs time — not how much SQL each one carries.
//
// This used to be the slowest thing the adapter did. PouchDB builds a
// map/reduce view by walking the database 50 documents at a time and handing
// ONE `_bulkDocs` call a bookkeeping record per source document — INCLUDING
// source documents that emit nothing into that view — plus the `_local/lastSeq`
// checkpoint. So one batch carries 51 local documents, and each was written on
// its own with a SELECT to read the stored revision followed by a separate
// write: about 102 sequential round trips per batch, per view. A first build
// over a few thousand documents owed tens of thousands of them.
//
// Planning and running are split so the batch path and the single-document
// `_putLocal` / `_removeLocal` paths issue the SAME statements and cannot drift
// apart. Planning is pure — no I/O — and running is one native call.

/**
 * Whether a planned local write stores the document or removes it.
 *
 * Passed in rather than read off `doc._deleted` because the two callers describe a
 * deletion differently: PouchDB routes `_removeLocal` a document with no `_deleted` flag
 * set, while a deletion arriving inside `_bulkDocs` carries one.
 */
enum LocalWriteIntent {
  Write,
  Delete,
}

/** What one `_local/` document write becomes: a statement, or an answer needing none. */
interface LocalWritePlan {
  /** the slot of the request's results array this document's outcome belongs in */
  resultIndex: number;
  /** null when the document can be answered without touching SQLite at all */
  statement: { sql: string; params: any[] } | null;
  /** reported when the statement changed a row */
  onApplied: any;
  /**
   * reported when the statement matched no row — built on demand because the
   * common case never needs it, and an Error carries a stack. A batch plans 51
   * of these at a time, which is the whole point of #4717.
   */
  makeRejection: () => any;
}

/**
 * Turn one local document into the single statement that writes it.
 *
 * The revision check lives in the statement's own `WHERE` clause rather than in
 * a `SELECT` before it. That removes a round trip AND closes a gap: the compare
 * now happens inside the write's transaction instead of in a window another
 * writer could commit into.
 */
function planLocalWrite(doc: any, resultIndex: number, intent: LocalWriteIntent): LocalWritePlan {
  const id = doc._id;
  const encodedId = sqlEncode(id);
  const oldRev = doc._rev;

  if (intent === LocalWriteIntent.Delete) {
    // a delete names the revision it removes. With no revision there is nothing
    // to match on, and the answer for that is the same as for a revision that
    // is not stored: the document is missing.
    if (!oldRev) {
      return { resultIndex, statement: null, onApplied: null, makeRejection: MISSING_DOC };
    }
    return {
      resultIndex,
      statement: {
        sql: `DELETE FROM local_store WHERE id = ? AND rev = ?`,
        params: [encodedId, oldRev]
      },
      onApplied: { ok: true, id, rev: '0-0' },
      makeRejection: MISSING_DOC,
    };
  }

  const newRev = oldRev ? '0-' + (parseInt(oldRev.split('-')[1], 10) + 1) : '0-1';

  // strip the fields PouchDB keeps outside the stored body
  const toStore = Object.assign({}, doc);
  delete toStore._id;
  delete toStore._rev;
  delete toStore._revisions;
  const json = JSON.stringify(toStore);

  if (oldRev) {
    return {
      resultIndex,
      statement: {
        sql: `UPDATE local_store SET rev = ?, json = ? WHERE id = ? AND rev = ?`,
        params: [newRev, json, encodedId, oldRev]
      },
      onApplied: { ok: true, id, rev: newRev },
      makeRejection: REV_CONFLICT,
    };
  }

  // `local_store` has exactly ONE constraint — `id TEXT PRIMARY KEY` — with no
  // UNIQUE, CHECK, NOT NULL, foreign key or trigger beside it (see SCHEMA_SQL).
  // So the only thing OR IGNORE can swallow is "that id is already stored",
  // which is precisely the conflict a create has to report.
  //
  // #3466: this is also why the losing side of two concurrent creates now gets
  // a NORMALIZED 409. The old shape read first and then inserted, so two cold
  // view builds both saw `_local/mrviews` as absent and both inserted; the
  // loser got a raw `UNIQUE constraint failed`, which PouchDB's upsert retry
  // does not recognise (it only retries a 409) and so surfaced to the caller.
  return {
    resultIndex,
    statement: {
      sql: `INSERT OR IGNORE INTO local_store (id, rev, json) VALUES (?, ?, ?)`,
      params: [encodedId, newRev, json]
    },
    onApplied: { ok: true, id, rev: newRev },
    makeRejection: REV_CONFLICT,
  };
}

/**
 * Run a planned group of local-document writes as ONE native call, writing each
 * document's outcome into its own slot of `results`.
 *
 * Statement order is document order, so two entries for the same id in one
 * group behave as they always did: they are consecutive statements in one
 * transaction, and the second sees what the first wrote.
 *
 * A stale revision is NOT an error here — it is a statement that matched no
 * row — so one refused document still leaves the rest of the group applied and
 * reports its own 409/404 in its own slot. Only the native call itself can
 * throw (a prepare/step failure, a full disk, a refused COMMIT), and then the
 * whole group rolls back and the error travels to the caller. That is
 * deliberate: a native failure writing `local_store` is the same class of
 * failure as one writing `doc_store`, which already propagates, and putting a
 * raw native Error into a per-document results slot — which is what this used
 * to do — hides it from everyone downstream.
 */
async function applyLocalWritesAsync(
  sqlite: CsSQLitePlugin,
  dbName: string,
  plans: LocalWritePlan[],
  results: any[],
): Promise<void> {
  const toRun: LocalWritePlan[] = [];
  for (const plan of plans) {
    if (plan.statement) {
      toRun.push(plan);
    } else {
      results[plan.resultIndex] = plan.makeRejection();
    }
  }
  if (toRun.length === 0) return;

  const multi = await sqlite.executeMulti({
    name: dbName,
    statements: toRun.map(plan => plan.statement!),
  });

  // one statement in, one result out, in the same order — so `toRun[i]` is the
  // document `multi.results[i]` answers. Getting this mapping wrong would swap
  // outcomes between documents while every row still looked plausible.
  for (let i = 0; i < toRun.length; i++) {
    const plan = toRun[i];
    results[plan.resultIndex] = multi.results[i].rowsAffected > 0
      ? plan.onApplied
      : plan.makeRejection();
  }
}

// ─── _bulkDocs write helpers ──────────────────────────────────────────
//
// These two do the work of one `_bulkDocs` call: `processWriteUnitAsync`
// merges one unit of incoming revisions into what is already stored, and
// `writeChunkAtomicallyAsync` commits the result. They live HERE, at module
// scope, rather than nested inside `api._bulkDocs`, so that what each one
// depends on is the short list in `BulkDocsContext` below instead of
// everything the request closure happens to have in scope. Nested, the only
// way to know what either one used was to read the whole enclosing function —
// and the #4417 change paid for that immediately: it threaded a `fetchedDocs`
// parameter into `writeChunkAtomicallyAsync` that was already dead, and only
// the review caught it.
//
// Two smaller helpers stay nested inside `processWriteUnitAsync` on purpose:
// they carry per-unit mutable state, so lifting them would mean threading a
// mutable state object around, which reads worse than the closure does.

interface WriteEntry { docInfo: any; winRev: string; winRevIsDeleted: boolean; newRevIsDeleted: boolean; isUpdate: boolean; index: number }

/**
 * Everything one `_bulkDocs` request hands to the two helpers below.
 *
 * `api` is passed as the object rather than as a copied `auto_compaction`
 * flag: PouchDB sets that property on the adapter AFTER it is constructed,
 * and the write path reads it once per document, so copying it would move
 * the read to a different moment than it happens today.
 */
interface BulkDocsContext {
  sqlite: CsSQLitePlugin;
  dbName: string;
  api: any;
  /** the request's own result array, written in place at each doc's index */
  results: any[];
  revLimit: number;
  newEdits: any;
  reportLocalCorruption: LocalCorruptionReporter;
}

// #4343: write one chunk of docs ATOMICALLY — every doc's body row
// (by_seq_store) and metadata row (doc_store) land in ONE native
// call, which the plugin runs as ONE SQLite transaction. The old
// shape wrote the bodies first, awaited a seq read-back on the JS
// side, and wrote the metadata in a SECOND transaction. In the gap
// between the two, a changes scan saw the body row (with a seq)
// but no metadata, skipped the doc, and still advanced past its
// seq — so the map/reduce checkpoint could pass a doc that was
// never indexed, dropping it from every view forever (the #4168
// empty-History-chart bug).
//
// The seq is derived with a SQL subquery instead of the JS
// read-back, and the assigned seqs are read back by SELECTs inside
// the SAME call (executeMulti returns rows per statement), so the
// in-memory metadata stays accurate without a second transaction.
async function writeChunkAtomicallyAsync(
  ctx: BulkDocsContext,
  chunk: { docIdRev: string; bodyJson: string; entry: WriteEntry }[],
  docCountDeltaToApply: number,
): Promise<void> {
  const statements: { sql: string; params?: any[] }[] = [];
  for (const p of chunk) {
    const { docInfo, winRev, winRevIsDeleted, isUpdate } = p.entry;
    const metadata = docInfo.metadata;

    statements.push({
      sql: `INSERT OR REPLACE INTO by_seq_store (doc_id_rev, json) VALUES (?, ?)`,
      params: [p.docIdRev, p.bodyJson]
    });

    // the `seq` embedded in the metadata json is a placeholder:
    // the seq COLUMN (filled by the subquery below) is the source
    // of truth, and decodeMetadata always overrides the json value
    // from the column when reading
    metadata.seq = 0;
    const encoded = encodeMetadata(metadata, winRev, winRevIsDeleted);
    statements.push({
      sql: `INSERT OR REPLACE INTO doc_store (id, json, winningRev, deleted, seq)
            VALUES (?, ?, ?, ?, (SELECT seq FROM by_seq_store WHERE doc_id_rev = ?))`,
      params: [encoded.id, encoded.json, encoded.winningRev, encoded.deleted, p.docIdRev]
    });

    // if auto-compaction is on, drop the body rows of revisions the
    // rev-tree no longer needs (stemmed ancestors + compactable
    // non-leaf revs) inside the same transaction
    if (isUpdate && ctx.api.auto_compaction) {
      for (const rev of (docInfo.stemmedRevs || []).concat(compactTree(metadata))) {
        statements.push({
          sql: `DELETE FROM by_seq_store WHERE doc_id_rev = ?`,
          params: [sqlEncode(metadata.id + '::' + rev)]
        });
      }
    }
  }

  // read the assigned seqs back inside the same transaction, so
  // each record's own metadata carries the seq it was actually
  // given rather than the 0 placeholder written above
  const seqSelectStart = statements.length;
  for (const p of chunk) {
    statements.push({
      sql: `SELECT seq FROM by_seq_store WHERE doc_id_rev = ?`,
      params: [p.docIdRev]
    });
  }

  if (docCountDeltaToApply !== 0) {
    statements.push({
      sql: `UPDATE meta_store SET value = CAST(CAST(value AS INTEGER) + ? AS TEXT) WHERE id = 'doc_count'`,
      params: [docCountDeltaToApply]
    });
  }

  const multi = await ctx.sqlite.executeMulti({ name: ctx.dbName, statements });

  for (let i = 0; i < chunk.length; i++) {
    const p = chunk[i];
    const metadata = p.entry.docInfo.metadata;
    const seqResult = multi.results[seqSelectStart + i] as ExecuteResult;
    // the row was inserted earlier in this same transaction, so a
    // missing read-back is a can't-happen state; the 0 fallback
    // only preserves the previous write paths' behavior
    metadata.seq = seqResult.rows[0]?.seq || 0;

    ctx.results[p.entry.index] = { ok: true, id: metadata.id, rev: metadata.rev };
  }
}

// #4417: ONE unit of work — read the current metadata for exactly
// these documents, merge the incoming revisions into it, and write
// the result back. The caller runs this inside writeSerializer, so
// no other write unit can commit between the read and the write and
// leave this unit merging into a revision list that no longer
// exists. Before this, the read happened once for the WHOLE call
// and the writes trailed it by chunks, so a doc in chunk 5 was
// merged against metadata read seconds earlier.
async function processWriteUnitAsync(ctx: BulkDocsContext, unit: { parsed: any; index: number }[]): Promise<void> {
  const { newEdits, results } = ctx;
  const fetchedDocs = new Map<string, any>();
  // ids whose stored winning revision has NO body row. Such a
  // document cannot be read at all (_get reports it missing), so
  // the write path must not treat it as a normal existing document
  // — that is what turns this into permanent, silent data loss.
  // id -> the STORED winning revision whose body row is missing.
  // Keyed on the stored winner on purpose: #4431 refreshes
  // `fetchedDocs` per row, so `prev.winningRev` can move on within
  // this unit, and the repair below must keep asking about the
  // revision whose body actually went missing.
  const missingWinningBody = new Map<string, string>();
  const uniqueIds = [...new Set(unit.map(d => d.parsed.metadata.id))];
  for (let c = 0; c < uniqueIds.length; c += 500) {
    const idChunk = uniqueIds.slice(c, c + 500);
    const placeholders = idChunk.map(() => '?').join(',');
    const res = await ctx.sqlite.execute({
      name: ctx.dbName,
      // the correlated subquery answers "does the winning revision
      // still have a body row?" in the SAME round-trip, so the
      // check below costs no extra native call. `doc_store.id` is
      // stored already sqlEncode'd, and a revision id never
      // contains the two escaped characters, so `id || '::' ||
      // winningRev` is exactly the by_seq_store key that
      // sqlEncode(id + '::' + rev) produces.
      sql: `SELECT d.*, (SELECT 1 FROM by_seq_store b WHERE b.doc_id_rev = d.id || '::' || d.winningRev) AS winning_body_present
            FROM doc_store d WHERE d.id IN (${placeholders})`,
      params: idChunk.map(sqlEncode)
    });
    for (const row of res.rows) {
      const meta = decodeMetadata(row);
      fetchedDocs.set(meta.id, meta);
      if (row.winningRev && row.winning_body_present == null) {
        missingWinningBody.set(meta.id, row.winningRev);
      }
    }
  }

  // THE OTHER HALF OF THE SAME HAZARD, fixed in #4431: two entries
  // for the SAME document id inside ONE unit. The exclusion above
  // stops two separate CALLERS interleaving; it does nothing about
  // two rows sitting in one unit, which used to be merged against
  // the same snapshot so the second could drop the first's
  // revision. `registerForLaterRowsInThisUnit` below now refreshes
  // that snapshot per row, so both cases are covered — and neither
  // fix covers the other's case. See design-docs/sync-integrity.md,
  // "What #4431 measured".
  let docCountDelta = 0;
  const toWrite: WriteEntry[] = [];
  // Repairs are reported only once their write has committed, so
  // the 'info' tier's rule holds literally: the repair HAS already
  // happened by the time the report runs (#4382, CLAUDE.md).
  const repairsToReport: { name: string; context: Record<string, unknown> }[] = [];

  /**
   * #4431: hand this row's merged result to any LATER row for the
   * same `_id` in this same unit, so that row merges against the
   * tree its predecessor built rather than the stale stored one.
   * Without it a merge that produces a NEW ROOT reaches only the
   * copy `doMerge` returns, and the sibling row's write drops it —
   * silently, with every row still reporting `ok`. Pull replication
   * makes that routine: it asks for one document per MISSING
   * REVISION and hands the whole result to one `bulkDocs`.
   *
   * `winningRev` and `deleted` MUST be normalised here, not left as
   * `parseDoc` wrote them: on a parsed row `deleted` means "THIS
   * revision is a deletion", while on stored metadata (and so to
   * every reader in the loop below) it means "the WINNER is
   * deleted".
   *
   * See design-docs/sync-integrity.md, "What #4431 measured".
   */
  function registerForLaterRowsInThisUnit(metadata: any, winRev: string, winRevIsDeleted: boolean): void {
    metadata.winningRev = winRev;
    metadata.deleted = winRevIsDeleted;
    fetchedDocs.set(metadata.id, metadata);
  }

  // Queue `docInfo` to be written as a brand-new revision tree.
  //
  // Two callers reach here and they differ only in what the
  // document counted as BEFORE. `previouslyDeleted` is null when
  // there was no stored document at all, so nothing was counted;
  // true or false when a doc_store row already existed and says
  // whether its winning revision was a tombstone. The repair path
  // is the one that makes this distinction matter: it recreates a
  // document that already HAS a row, so counting it as new inflates
  // `doc_count` — the number `_info()` and `_allDocs`' `total_rows`
  // report — by one on every repair, permanently.
  function pushAsFreshCreate(docInfo: any, index: number, previouslyDeleted: boolean | null): void {
    const merged = merge([], docInfo.metadata.rev_tree[0], ctx.revLimit);
    docInfo.metadata.rev_tree = merged.tree;
    docInfo.stemmedRevs = merged.stemmedRevs || [];

    const winRev = winningRev(docInfo.metadata);
    const deleted = isDeleted(docInfo.metadata, winRev);
    // doc_count counts documents whose winning revision is live
    const liveBefore = previouslyDeleted === null || previouslyDeleted ? 0 : 1;
    const liveAfter = deleted ? 0 : 1;
    docCountDelta += liveAfter - liveBefore;

    toWrite.push({ docInfo, winRev, winRevIsDeleted: deleted, newRevIsDeleted: deleted, isUpdate: false, index });
    // registering it here is also what stops a second row for the
    // same NEW id being counted as a second document in `doc_count`
    registerForLaterRowsInThisUnit(docInfo.metadata, winRev, deleted);
  }

  for (const { parsed, index } of unit) {
    const docInfo = parsed;
    const id = docInfo.metadata.id;
    // true when this document is currently unreadable — see
    // missingWinningBody above
    const bodylessStoredWinner = missingWinningBody.get(id);
    const winnerBodyGone = bodylessStoredWinner !== undefined;

    if (fetchedDocs.has(id)) {
      const prev = fetchedDocs.get(id);

      if (revExists(prev.rev_tree, docInfo.metadata.rev) && newEdits === false) {
        // normally nothing to do — we already hold this revision.
        // The one exception is a document whose winning revision
        // lost its body: reporting success here is exactly what
        // makes that permanent, because sync then never re-sends
        // the one body that would repair it. Fall through and
        // write, but only when the incoming revision IS the winner
        // (storing a non-winning body repairs nothing).
        if (!(winnerBodyGone && docInfo.metadata.rev === bodylessStoredWinner)) {
          results[index] = { ok: true, id, rev: docInfo.metadata.rev };
          continue;
        }
        repairsToReport.push({
          name: 'winning-body-restored-by-sync',
          context: { docId: id, winningRev: bodylessStoredWinner },
        });
      }

      const previousWinningRev = prev.winningRev || winningRev(prev);
      const previouslyDeleted = 'deleted' in prev ? prev.deleted : isDeleted(prev, previousWinningRev);
      const deleted = 'deleted' in docInfo.metadata ? docInfo.metadata.deleted : isDeleted(docInfo.metadata);
      const isRoot = /^1-/.test(docInfo.metadata.rev);

      if (previouslyDeleted && !deleted && newEdits !== false && isRoot) {
        const newDoc = docInfo.data;
        newDoc._rev = previousWinningRev;
        newDoc._id = docInfo.metadata.id;
        const reparsed = parseDoc(newDoc, true);
        if (reparsed) {
          docInfo.metadata = reparsed.metadata;
          docInfo.data = reparsed.data;
        }
      }

      const merged = merge(prev.rev_tree, docInfo.metadata.rev_tree[0], ctx.revLimit);

      const inConflict = newEdits !== false && ((
        (previouslyDeleted && deleted && merged.conflicts !== 'new_leaf') ||
        (!previouslyDeleted && merged.conflicts !== 'new_leaf') ||
        (previouslyDeleted && !deleted && merged.conflicts === 'new_branch')));

      if (inConflict) {
        if (winnerBodyGone) {
          // Refusing here is what stranded a user's whole workout
          // (#4417). The stored metadata describes a revision that
          // cannot be read, so there is no `_rev` any caller could
          // supply that would ever be accepted, and the document is
          // stuck for good. Drop the unusable metadata and let this
          // write recreate the document instead.
          //
          // The trade-off, stated plainly: the local revision
          // history is discarded, so the copy on the server (at a
          // higher generation) may win the resulting conflict on
          // the next sync. That is still far better than the
          // alternative, which is refusing every save forever and
          // losing the data outright.
          //
          // ORDER MATTERS BELOW. Refuse first, and only then record
          // the repair and drop the stale metadata. Doing it the
          // other way round reports a landed repair for a document
          // this entry never wrote, and leaves a second entry for
          // the same id in this batch merging against nothing.
          if (docInfo.metadata.rev_tree[0].ids[1].status === 'missing') {
            // the caller wrote against a revision we cannot honour
            // any more; its retry as a plain create takes the
            // repair path on the next call
            results[index] = REV_CONFLICT();
            continue;
          }
          repairsToReport.push({
            // the STORED bodyless winner, not `prev.winningRev`,
            // which an earlier row in this unit may have moved on
            name: 'recreated-over-unreadable-doc',
            context: { docId: id, winningRev: bodylessStoredWinner },
          });
          fetchedDocs.delete(id);
          missingWinningBody.delete(id);
          pushAsFreshCreate(docInfo, index, previouslyDeleted);
          continue;
        }
        results[index] = REV_CONFLICT();
        continue;
      }

      docInfo.metadata.rev_tree = merged.tree;
      docInfo.stemmedRevs = merged.stemmedRevs || [];

      const winRev = winningRev(docInfo.metadata);
      const winRevIsDeleted = isDeleted(docInfo.metadata, winRev);
      const delta = (previouslyDeleted === winRevIsDeleted) ? 0 :
        previouslyDeleted < winRevIsDeleted ? -1 : 1;
      const newRev = docInfo.metadata.rev;
      const newRevIsDeleted = newRev === winRev ? winRevIsDeleted : isDeleted(docInfo.metadata, newRev);

      docCountDelta += delta;
      toWrite.push({ docInfo, winRev, winRevIsDeleted, newRevIsDeleted, isUpdate: true, index });
      // #4431 — see the note on the helper
      registerForLaterRowsInThisUnit(docInfo.metadata, winRev, winRevIsDeleted);
    } else {
      if (newEdits !== false && docInfo.metadata.rev_tree[0].ids[1].status === 'missing') {
        results[index] = REV_CONFLICT();
        continue;
      }
      // no stored document, so nothing was counted before
      pushAsFreshCreate(docInfo, index, null);
    }
  }

  // a repair only ever ends up in `repairsToReport` alongside a
  // `toWrite` entry, so there is no path where a repair is claimed
  // while nothing is written
  if (toWrite.length === 0) return;

  const prepared: { docIdRev: string; bodyJson: string; entry: WriteEntry }[] = [];
  for (const entry of toWrite) {
    const { docInfo, winRev, winRevIsDeleted, newRevIsDeleted } = entry;
    const doc = docInfo.data;
    const metadata = docInfo.metadata;

    doc._id = metadata.id;
    doc._rev = metadata.rev;
    if (newRevIsDeleted) doc._deleted = true;
    metadata.winningRev = winRev;
    metadata.deleted = winRevIsDeleted;

    const docIdRev = sqlEncode(metadata.id + '::' + metadata.rev);
    const bodyToStore: any = {};
    for (const key in doc) {
      if (key !== '_id' && key !== '_rev') bodyToStore[key] = doc[key];
    }

    prepared.push({ docIdRev, bodyJson: JSON.stringify(bodyToStore), entry });
  }

  await writeChunkAtomicallyAsync(ctx, prepared, docCountDelta);

  // the write committed, so every repair above has now actually
  // landed — which is what lets these report at the 'info' tier
  for (const repair of repairsToReport) {
    ctx.reportLocalCorruption(repair.name, repair.context, 'info');
  }
}

// ─── Adapter Constructor ──────────────────────────────────────────────

export function CapacitorSQLitePouch(this: any, opts: any, callback: (err: any, api?: any) => void) {
  // eslint-disable-next-line @typescript-eslint/no-this-alias
  const api: any = this;
  const dbName: string = opts.name;
  // test seam: unit tests inject a fake plugin (backed by an in-memory SQLite)
  // via opts.sqlitePlugin. Production always uses the real native plugin.
  const sqlite: CsSQLitePlugin = opts.sqlitePlugin || CsSQLite;
  let closed = false;
  const writeGate = new WritePriorityGate();
  // serializes each write unit's read-merge-write against every other one
  // (#4417) — see WriteSerializer above for why this cannot live in SQL
  const writeSerializer = new WriteSerializer();
  /** See LocalCorruptionReporter in ./local-corruption-reporter for the
   *  contract. Injected by the caller (getLocalAdapterOpts in
   *  local-database-engine.ts) because the adapter cannot import LoggingService —
   *  database-recovery.service.ts imports from this directory and logging.service.ts
   *  imports that, so naming the class here would close a cycle. Falls back to
   *  console-only when nothing is wired, which is what the
   *  unit tests get. */
  const reportLocalCorruption: LocalCorruptionReporter =
    opts.onLocalCorruption || ((name, context, severity) => {
      const line = `CapacitorSQLitePouch: local corruption '${name}' ${JSON.stringify(context)}`;
      if (severity === 'info') console.log(line); else console.error(line);
    });

  /** See LocalAdapterFaultReporter in ./local-corruption-reporter. Injected the
   *  same way, by the same callers, for the same import-cycle reason. Falls
   *  back to console-only when nothing is wired, which is what the unit tests
   *  get. */
  const reportAdapterFault: LocalAdapterFaultReporter =
    opts.onAdapterFault || ((name, context) => {
      console.error(`CapacitorSQLitePouch: adapter fault '${name}' ${JSON.stringify(context)}`);
    });

  // changesListeners: for live changes feed. `poll` is invoked event-driven by
  // notifyChanges() on every write; the interval is only a slow safety net.
  const changesListeners: Map<string, { interval: any, cancelled: boolean, poll: () => void }> = new Map();
  // set once notifyChanges() has reported a synchronous poll() throw — see there
  let notifyPollThrowReported = false;
  // ids `_allDocs` has already reported as torn (winner with no body row), so
  // repeated reads of one damaged document file one report, not one per read
  const allDocsTornReported = new Set<string>();

  api._remote = false;
  api.type = () => 'capacitor-sqlite';

  void (async function init() {
    try {
      // L11 (#3986): bound the native open. A hung CsSQLite.open used to leave every DB op
      // pending forever (SQLite has no IndexedDB-style recovery). On timeout this rejects with a
      // distinguishable SqliteOpenTimeout error, which flows through the catch below into
      // callback(e); DatabaseRecoveryService.interceptDatabaseError classifies it, reports SqliteOpenTimeout,
      // and routes to the reload path instead of a silent forever-hang.
      await withSqliteOpenTimeout(sqlite.open({ name: dbName }), dbName, SQLITE_OPEN_TIMEOUT_MS, setTimeout, clearTimeout);

      // create schema
      await sqlite.executeBatch({
        name: dbName,
        statements: SCHEMA_SQL.map(sql => ({ sql }))
      });

      // ensure db_id exists
      const metaResult = await sqlite.execute({
        name: dbName,
        sql: `SELECT value FROM meta_store WHERE id = 'db_id'`
      });
      let instanceId: string;
      if (metaResult.rows.length === 0) {
        instanceId = generateUUID();
        await sqlite.executeBatch({
          name: dbName,
          statements: [{ sql: `INSERT INTO meta_store (id, value) VALUES ('db_id', ?)`, params: [instanceId] }]
        });
      } else {
        instanceId = metaResult.rows[0].value;
      }

      // ensure doc_count exists
      const countResult = await sqlite.execute({
        name: dbName,
        sql: `SELECT value FROM meta_store WHERE id = 'doc_count'`
      });
      if (countResult.rows.length === 0) {
        await sqlite.executeBatch({
          name: dbName,
          statements: [{ sql: `INSERT INTO meta_store (id, value) VALUES ('doc_count', '0')` }]
        });
      }

      api._meta = { instanceId, name: dbName };

      // ─── api._id ─────────────────────────────────────────────
      api._id = function (cb: any) {
        cb(null, instanceId);
      };

      // ─── api._info ────────────────────────────────────────────
      api._info = function (cb: (err: any, info?: any) => void) {
        void (async () => {
          try {
            const countRes = await sqlite.execute({
              name: dbName,
              sql: `SELECT value FROM meta_store WHERE id = 'doc_count'`
            });
            const docCount = parseInt(countRes.rows[0]?.value ?? '0', 10);

            const seqRes = await sqlite.execute({
              name: dbName,
              sql: `SELECT MAX(seq) as max_seq FROM by_seq_store`
            });
            const updateSeq = seqRes.rows[0]?.max_seq ?? 0;

            cb(null, {
              doc_count: docCount,
              update_seq: updateSeq,
              sqlite: true
            });
          } catch (e) {
            cb(e);
          }
        })();
      };

      // ─── api._get ─────────────────────────────────────────────
      api._get = function (id: string, reqOpts: any, cb: (err: any, result?: any) => void) {
        void (async () => {
          // S3 (#3986): count this read for the boot READ-priority gate —
          // during the boot window the sync chunk loop yields to pending
          // reads (see boot-read-priority.ts). No-cost outside boot.
          bootReadPriorityGate.enterRead();
          try {
            // #6351: the metadata and the body come from ONE statement. Read in
            // two native calls (two transactions), a save landing in between
            // could replace the winner — auto-compaction then correctly deletes
            // the old winner's body — and the second read answered 404 for a
            // healthy document. With no `rev` the body joins on the stored
            // winner; with a `rev` it joins on the requested revision.
            const docRes = await sqlite.execute({
              name: dbName,
              sql: reqOpts.rev
                ? `SELECT d.*, b.json AS body_json FROM doc_store d
                   LEFT JOIN by_seq_store b ON b.doc_id_rev = ? WHERE d.id = ?`
                : `SELECT d.*, b.json AS body_json FROM doc_store d
                   LEFT JOIN by_seq_store b ON b.doc_id_rev = d.id || '::' || d.winningRev WHERE d.id = ?`,
              params: reqOpts.rev
                ? [sqlEncode(id + '::' + reqOpts.rev), sqlEncode(id)]
                : [sqlEncode(id)]
            });
            if (docRes.rows.length === 0) {
              return cb(MISSING_DOC());
            }

            const row = docRes.rows[0];
            const metadata = decodeMetadata(row);
            let rev: string;

            if (!reqOpts.rev) {
              rev = metadata.winningRev;
              if (isDeleted(metadata)) {
                const err = MISSING_DOC();
                err.message = 'deleted';
                err.reason = 'deleted';
                return cb(err);
              }
            } else {
              rev = reqOpts.latest ? latest(reqOpts.rev, metadata) : reqOpts.rev;
            }

            const key = sqlEncode(metadata.id + '::' + rev);
            let bodyJson: string | null = row.body_json ?? null;
            if (reqOpts.rev && rev !== reqOpts.rev) {
              // `latest` moved past the requested revision, and which revision
              // it lands on depends on the tree just read, so the join could not
              // name it. This rare path keeps a second read and its small gap.
              const seqRes = await sqlite.execute({
                name: dbName,
                sql: `SELECT json FROM by_seq_store WHERE doc_id_rev = ?`,
                params: [key]
              });
              bodyJson = seqRes.rows.length > 0 ? seqRes.rows[0].json : null;
            }

            if (bodyJson == null) {
              return cb(MISSING_DOC());
            }

            const doc = decodeDoc({ doc_id_rev: key, json: bodyJson });
            cb(null, { doc, metadata, ctx: null });
          } catch (e) {
            cb(e);
          } finally {
            bootReadPriorityGate.exitRead();
          }
        })();
      };

      // ─── api._bulkDocs ────────────────────────────────────────
      api._bulkDocs = function (req: any, reqOpts: any, cb: (err: any, results?: any) => void) {
        void (async () => {
          const docs: any[] = req.docs;
          const newEdits = reqOpts.new_edits;
          const results: any[] = new Array(docs.length);
          const revLimit = opts.revs_limit || 1000;
          // user writes are small (1-5 docs), sync writes are large (10+)
          const isUserWrite = docs.length <= 5;
          if (isUserWrite) writeGate.enterUserWrite();

          try {
            // parse all non-local docs
            const docInfos: any[] = [];
            // #4717: local docs are PLANNED here and written together, in one
            // native call, after the loop below. They used to be written one at
            // a time right here, at two round trips each.
            const localPlans: LocalWritePlan[] = [];
            for (let i = 0; i < docs.length; i++) {
              const doc = docs[i];
              if (doc._id && isLocalId(doc._id)) {
                localPlans.push(planLocalWrite(doc, i, doc._deleted ? LocalWriteIntent.Delete : LocalWriteIntent.Write));
                continue;
              }

              const parsed = parseDoc(doc, newEdits !== false);
              if (!parsed) {
                results[i] = pouchError(400, 'bad_request', 'Invalid rev format');
                continue;
              }
              docInfos.push({ parsed, index: i });
            }

            // Everything the two module-scope write helpers need. Building it
            // once here is what lets them live outside this closure — see
            // BulkDocsContext above.
            const writeContext: BulkDocsContext = {
              sqlite, dbName, api, results, revLimit, newEdits, reportLocalCorruption,
            };

            // split the work into units and run them one at a time.
            // A user save is one unit. A sync batch is split so it can still
            // yield to user saves BETWEEN units — that yielding is deliberate
            // (#3986) and must not be swallowed by the serializer, so it
            // happens outside it.
            const CHUNK_SIZE = 10;
            const units: { parsed: any; index: number }[][] = [];
            if (isUserWrite) {
              if (docInfos.length > 0) units.push(docInfos);
            } else {
              for (let c = 0; c < docInfos.length; c += CHUNK_SIZE) {
                units.push(docInfos.slice(c, c + CHUNK_SIZE));
              }
            }

            for (let u = 0; u < units.length; u++) {
              if (u > 0) {
                // yield 50ms between sync chunks to let user saves/reads/navigation
                // execute on the JS event loop — prevents sync from starving the UI
                await new Promise(resolve => setTimeout(resolve, 50));
                await writeGate.waitForUserWrites();
                // S3 (#3986): during the boot window, also yield to pending
                // READS — boot-critical reads (singletons, plan, views) used
                // to queue behind these write batches on the one native
                // bridge. No-op outside boot (see boot-read-priority.ts,
                // incl. the failsafe that bounds a never-ending window).
                await bootReadPriorityGate.waitForPendingBootReads();
              }
              const unit = units[u];
              await writeSerializer.runExclusiveAsync(() => processWriteUnitAsync(writeContext, unit));
            }

            // #4717: AFTER the ordinary documents, never before them.
            // `_local/lastSeq` is the map/reduce checkpoint — "everything up to
            // sequence N is indexed" — and it rides in the same batch as the
            // index rows it vouches for. Written first (as it was), an app
            // killed in the gap left a checkpoint claiming work that never
            // landed, and mapreduce never revisits a sequence it has checkpointed
            // past, so those documents stayed invisible to that view. That is the
            // same failure the #4343 atomic-chunk work removed for doc_store.
            // The opposite crash is harmless: index rows written and the
            // checkpoint not advanced just means the next build redoes the batch.
            //
            // This does NOT need the writeSerializer. That exists to stop two
            // callers interleaving a read-merge-write of doc_store across
            // separate native calls; here the revision check rides inside the
            // one statement, so there is no read to interleave with.
            await applyLocalWritesAsync(sqlite, dbName, localPlans, results);

            if (isUserWrite) writeGate.exitUserWrite();
            cb(null, results);

            // notify changes listeners
            notifyChanges();
          } catch (e) {
            if (isUserWrite) writeGate.exitUserWrite();
            cb(e);
          }
        })();
      };

      // ─── api._allDocs ─────────────────────────────────────────
      api._allDocs = function (allDocsOpts: any, cb: (err: any, result?: any) => void) {
        void (async () => {
          // S3 (#3986): count this read for the boot READ-priority gate
          // (same as _get above)
          bootReadPriorityGate.enterRead();
          try {
            const start = 'startkey' in allDocsOpts ? allDocsOpts.startkey : undefined;
            const end = 'endkey' in allDocsOpts ? allDocsOpts.endkey : undefined;
            const key = 'key' in allDocsOpts ? allDocsOpts.key : undefined;
            const keys = 'keys' in allDocsOpts ? allDocsOpts.keys : undefined;
            const skip = allDocsOpts.skip || 0;
            const limit = typeof allDocsOpts.limit === 'number' ? allDocsOpts.limit : -1;
            const inclusiveEnd = allDocsOpts.inclusive_end !== false;
            const descending = allDocsOpts.descending;

            // get doc_count
            const countRes = await sqlite.execute({
              name: dbName,
              sql: `SELECT value FROM meta_store WHERE id = 'doc_count'`
            });
            const totalRows = parseInt(countRes.rows[0]?.value ?? '0', 10);

            let rows: any[];

            // #6351: with include_docs, each winner's body comes back in the
            // same statement as its doc_store row (`body_json`), never in a
            // later call. A save landing in between could replace the winner —
            // auto-compaction then deletes the old winner's body — and the row
            // came back with an empty `{}` for a healthy document. Without
            // include_docs no body is joined, so plain listings cost nothing more.
            const selectFrom = allDocsOpts.include_docs
              ? `SELECT d.*, b.json AS body_json FROM doc_store d
                 LEFT JOIN by_seq_store b ON b.doc_id_rev = d.id || '::' || d.winningRev`
              : `SELECT d.* FROM doc_store d`;

            if (keys) {
              // specific keys
              rows = [];
              for (const k of keys) {
                const res = await sqlite.execute({
                  name: dbName,
                  sql: `${selectFrom} WHERE d.id = ?`,
                  params: [sqlEncode(k)]
                });
                if (res.rows.length === 0) {
                  rows.push({ key: k, error: 'not_found' });
                } else {
                  rows.push(res.rows[0]);
                }
              }
            } else {
              // range query
              let sql = selectFrom;
              const params: any[] = [];
              // #6331: drop deleted rows in SQL, BEFORE the LIMIT below. A range read never
              // returns them (the loop further down skips them too, kept as a guard), but left in
              // they used up the limit, so `limit: 1` came back empty whenever the first row in
              // range was a deletion marker. Map/reduce leaves those in every view index.
              // `IS NOT 1` mirrors decodeMetadata's `row.deleted === 1`, so even a NULL can never
              // hide a live document.
              const conditions: string[] = [`deleted IS NOT 1`];

              if (key !== undefined) {
                conditions.push(`id = ?`);
                params.push(sqlEncode(key));
              } else {
                // #6331: with descending, PouchDB's contract makes startkey the HIGH bound and
                // endkey the LOW bound, and inclusive_end applies to whichever bound endkey is
                // (createKeyRange in PouchDB's IndexedDB adapter is the reference). Map/reduce
                // builds its descending view queries this way, so getting it wrong empties
                // every newest-first view query.
                if (start !== undefined) {
                  conditions.push(descending ? `id <= ?` : `id >= ?`);
                  params.push(sqlEncode(start));
                }
                if (end !== undefined) {
                  if (descending) {
                    conditions.push(inclusiveEnd ? `id >= ?` : `id > ?`);
                  } else {
                    conditions.push(inclusiveEnd ? `id <= ?` : `id < ?`);
                  }
                  params.push(sqlEncode(end));
                }
              }

              sql += ` WHERE ` + conditions.join(' AND ');
              sql += ` ORDER BY id ${descending ? 'DESC' : 'ASC'}`;
              if (limit >= 0) {
                sql += ` LIMIT ?`;
                params.push(limit + skip);
              }

              const res = await sqlite.execute({ name: dbName, sql, params });
              rows = res.rows;
            }

            const results: any[] = [];
            let skipped = 0;

            for (const row of rows) {
              if (row.error) {
                results.push(row);
                continue;
              }

              const metadata = decodeMetadata(row);
              const winRev = metadata.winningRev;
              const deleted = metadata.deleted;

              if (deleted) {
                if (keys) {
                  results.push({
                    id: metadata.id,
                    key: metadata.id,
                    value: { rev: winRev, deleted: true },
                    doc: null
                  });
                }
                continue;
              }

              if (skipped < skip) {
                skipped++;
                continue;
              }

              if (limit >= 0 && results.length >= limit) {
                break;
              }

              const result: any = {
                id: metadata.id,
                key: metadata.id,
                value: { rev: winRev }
              };

              if (allDocsOpts.include_docs) {
                // the body was joined onto this row in the same statement (#6351)
                if (row.body_json != null) {
                  result.doc = decodeDoc({ doc_id_rev: sqlEncode(metadata.id + '::' + winRev), json: row.body_json });
                  if (allDocsOpts.conflicts) {
                    const conflicts = collectConflicts(metadata);
                    if (conflicts.length) {
                      result.doc._conflicts = conflicts;
                    }
                  }
                } else {
                  // #4417: the winner has no body row, read in the same statement as
                  // its metadata, so this is real damage, not a race. Keep `{}` and
                  // report it once per document — see sync-integrity.md, "Telemetry".
                  if (!allDocsTornReported.has(metadata.id)) {
                    allDocsTornReported.add(metadata.id);
                    reportLocalCorruption('winning-body-missing-in-all-docs', {
                      docId: metadata.id,
                      winningRev: winRev,
                      leafCount: collectLeaves(metadata.rev_tree).length,
                    }, 'problem');
                  }
                  result.doc = {};
                }
              }

              results.push(result);
            }

            cb(null, {
              total_rows: totalRows,
              offset: skip,
              rows: results
            });
          } catch (e) {
            cb(e);
          } finally {
            bootReadPriorityGate.exitRead();
          }
        })();
      };

      // ─── api._changes ─────────────────────────────────────────
      api._changes = function (changesOpts: any) {
        changesOpts = Object.assign({}, changesOpts);
        changesOpts.since = changesOpts.since || 0;

        if (changesOpts.continuous) {
          return setupLiveChanges(changesOpts);
        }

        // one-shot changes
        void doChanges(changesOpts);

        return { cancel: () => {} };
      };

      // Turns one row of the changes query (a by_seq_store row joined to its doc_store
      // metadata) into the change the feed reports. Everything about which rows are read,
      // filtered, counted and reported stays in doChanges.
      async function buildChangeFromRow(row: any, changesOpts: any): Promise<any> {
        // the join guarantees this row's doc_store metadata exists and
        // that it is the doc's most recently WRITTEN revision
        const rowDoc = decodeDoc({ doc_id_rev: row.doc_id_rev, json: row.json });
        const metadata = decodeMetadata({
          json: row.meta_json,
          winningRev: row.winningRev,
          deleted: row.deleted,
          seq: row.seq
        });

        // usually the most recent write IS the winning revision — but when
        // the last write was a LOSING revision (a replicated rev that lost a
        // conflict, or a tombstone closing a losing branch), doc_store.seq
        // points at the loser's row. the change must carry the WINNING
        // revision's body, which the row query fetched in the same statement
        // (#6351 — see the query in doChanges for why it must not be a
        // second native call)
        let winningDoc = rowDoc;
        if (metadata.winningRev !== rowDoc._rev) {
          if (row.winning_body_present != null) {
            winningDoc = decodeDoc({
              doc_id_rev: sqlEncode(rowDoc._id + '::' + metadata.winningRev),
              json: row.winning_json,
            });
          } else {
            // #4417: this is the corrupt state, seen from the read side —
            // a winning revision with no body row, read in ONE statement
            // with the row itself, so no save can have landed in between.
            // It was described here as a can't-happen state, and it
            // happened: a lost update between two concurrent write units
            // could delete the body of a revision that the surviving
            // revision list still called the winner. `WriteSerializer`
            // closes that route (see its own comment for what it does not
            // cover); `_bulkDocs` repairs a document already in it. Keep the
            // feed alive on the scanned row's body and report the sighting.
            // 'problem', not 'info': nothing repaired this. The feed falls
            // back to the scanned row's body so it keeps moving, but the
            // document is still unreadable and someone should look.
            //
            // Until #6351 the winner was read in a SECOND native call, and
            // every production report of this name came from a save landing
            // between the two reads, not from damage. `scannedRev` and
            // `leafCount` are there to tell a future report apart.
            reportLocalCorruption('winning-body-missing-in-changes-feed', {
              docId: rowDoc._id,
              winningRev: metadata.winningRev,
              scannedRev: rowDoc._rev,
              leafCount: collectLeaves(metadata.rev_tree).length,
            }, 'problem');
          }
        }

        const changeList = changesOpts.style === 'all_docs'
          ? collectLeaves(metadata.rev_tree).map((x: any) => ({ rev: x.rev }))
          : [{ rev: winningDoc._rev }];

        // #4798: the body goes on the change unconditionally, because the filter
        // needs it — a selector filters on fields, and core does not set
        // `include_docs` for one. `applyFilter` deletes it again when the caller
        // did not ask for bodies, exactly as PouchDB does.
        const change: any = {
          id: metadata.id,
          changes: changeList,
          doc: winningDoc,
          seq: metadata.seq
        };

        if (isDeleted(metadata, winningDoc._rev)) {
          change.deleted = true;
        }
        return change;
      }

      async function doChanges(changesOpts: any) {
        try {
          const since = changesOpts.since || 0;
          const limit = 'limit' in changesOpts ? changesOpts.limit : -1;
          const descending = changesOpts.descending;
          const docIds: string[] | null = changesOpts.doc_ids ? [...new Set<string>(changesOpts.doc_ids)] : null;
          // #4798: a caller-supplied filter (a plain function, or a Mango selector that
          // PouchDB's core has already turned into one) cannot be expressed in SQL, so it
          // is applied per row below. That breaks the SQL `LIMIT`: it counts SCANNED rows,
          // while `limit` means REPORTABLE changes, so a batch shortened by filtering would
          // read as end-of-feed — the same misread described just below.
          //
          // The answer is a scan WINDOW rather than no bound at all. A filtered read walks
          // the feed a window at a time and stops as soon as it has enough reportable rows;
          // "fewer rows SCANNED than the window asked for" still honestly means the feed is
          // drained, because filtering cannot shorten a window. Removing the bound instead
          // would make every filtered call materialise the rest of the store, bodies and
          // all, before this loop decoded its first row — and replication asks for one
          // batch at a time from a moving `since`, so a push over N documents would read
          // about N-squared row bodies across the Capacitor bridge.
          const applyFilter = filterChange(changesOpts);
          const hasFilter = typeof changesOpts.filter === 'function';

          // #4343: filter in SQL, apply LIMIT after. PouchDB's map/reduce
          // indexer treats "fewer rows than limit" as "feed drained, stop" and
          // persists the last reported seq as its forever-checkpoint. So every
          // row this query returns MUST be a real, reportable change — the old
          // shape (raw by_seq_store scan, filter in JS afterwards) let a
          // window of superseded rows read as end-of-feed and let the
          // checkpoint skip past docs, permanently dropping them from every
          // view (the #4168 empty-History-chart bug).
          //
          // The join on doc_store.seq = by_seq_store.seq returns exactly one
          // row per doc — its most recently WRITTEN revision — and only once
          // its metadata is committed, so no JS-side skipping remains. The
          // most recent write is usually also the winning revision, so the row
          // usually carries the winning body and metadata in one go; for a
          // conflict row (the last write lost to an existing branch) the
          // winning body comes from the subqueries below, in this same
          // statement. The doc_store_seq_idx index (see SCHEMA_SQL) keeps this join from
          // scanning doc_store per row.
          //
          // #6351: for a conflict row the WINNER's body rides in this same
          // statement (`winning_json`, with `winning_body_present` saying
          // whether it exists), never in a second native call. Each call is its
          // own transaction, so a save landing between two calls could replace
          // the winner — auto-compaction then correctly deletes the old winner's
          // body — and the second read found nothing on a healthy document. The
          // key form is the one processWriteUnitAsync's subquery uses, and its
          // comment says why it equals sqlEncode(id + '::' + rev).
          let sql = `SELECT s.seq AS seq, s.doc_id_rev AS doc_id_rev, s.json AS json,
                            d.json AS meta_json, d.winningRev AS winningRev, d.deleted AS deleted,
                            CASE WHEN s.doc_id_rev = d.id || '::' || d.winningRev THEN NULL
                                 ELSE (SELECT w.json FROM by_seq_store w WHERE w.doc_id_rev = d.id || '::' || d.winningRev)
                            END AS winning_json,
                            CASE WHEN s.doc_id_rev = d.id || '::' || d.winningRev THEN 1
                                 ELSE (SELECT 1 FROM by_seq_store w WHERE w.doc_id_rev = d.id || '::' || d.winningRev)
                            END AS winning_body_present
                     FROM by_seq_store s JOIN doc_store d ON d.seq = s.seq
                     WHERE s.seq > ?`;
          const params: any[] = [since];

          if (docIds) {
            // the doc_ids filter must also live in SQL — filtering after the
            // LIMIT would recreate the same short-batch misread
            sql += ` AND d.id IN (${docIds.map(() => '?').join(',')})`;
            params.push(...docIds.map(sqlEncode));
          }

          // PouchDB treats `limit: 0` as 1, so keep that mapping.
          const reportableLimit = limit >= 0 ? (limit === 0 ? 1 : limit) : -1;
          // How many rows one query may read. Without a filter every scanned row is
          // reported, so the caller's own limit is the right bound and the behaviour is
          // exactly what it was before #4798 — including "no limit means one unbounded
          // query", which is what the map/reduce indexer has always asked for. With a
          // filter the window is at least the caller's limit, so a filter that keeps most
          // rows still finishes in one query.
          const scanWindow = hasFilter
            ? Math.max(reportableLimit, CHANGES_FILTERED_SCAN_WINDOW)
            : reportableLimit;

          const results: any[] = [];
          let lastSeq = since;
          // how many changes have actually been REPORTED — what `limit` bounds
          let reportedCount = 0;
          // `lastSeq` doubles as the cursor the next window starts after, which works
          // because it moves on every row SCANNED rather than every row reported — a
          // window whose rows are all filtered out must still make progress. This flag is
          // what tells "lastSeq is a row we have read" from its `since` starting value.
          let scannedAnyRow = false;
          let feedDrained = false;

          while (!feedDrained) {
            let windowSql = sql;
            const windowParams = [...params];
            if (scannedAnyRow) {
              // walk on from where the last window stopped, in whichever direction this
              // read is going
              windowSql += descending ? ` AND s.seq < ?` : ` AND s.seq > ?`;
              windowParams.push(lastSeq);
            }
            windowSql += ` ORDER BY s.seq ${descending ? 'DESC' : 'ASC'}`;
            if (scanWindow >= 0) {
              windowSql += ` LIMIT ?`;
              windowParams.push(scanWindow);
            }

            const seqRes = await sqlite.execute({ name: dbName, sql: windowSql, params: windowParams });
            // A window the query could not fill means there is nothing left behind it.
            // Filtering cannot shorten a window, so this stays an honest end-of-feed
            // signal — which is the whole reason the limit is applied per WINDOW and the
            // caller's limit is counted separately, below.
            if (scanWindow < 0 || seqRes.rows.length < scanWindow) {
              feedDrained = true;
            }

            for (const row of seqRes.rows) {
              const change = await buildChangeFromRow(row, changesOpts);

              // Advance the checkpoint on every row SCANNED, not only on the ones
              // reported — otherwise a batch whose tail is entirely filtered out hands
              // back a lower `last_seq` and the next scan re-reads the same rows for ever.
              // This is also the cursor the next window starts after.
              lastSeq = change.seq;
              scannedAnyRow = true;

              const filtered = applyFilter(change);
              // anything but a boolean is the error from a filter that threw: the caller
              // asked a question this feed cannot answer, so fail rather than silently
              // reporting everything
              if (typeof filtered !== 'boolean') {
                if (changesOpts.complete) changesOpts.complete(filtered);
                return;
              }
              if (!filtered) continue;

              if (changesOpts.onChange) {
                changesOpts.onChange(change);
              }
              if (changesOpts.return_docs !== false) {
                results.push(change);
              }
              reportedCount++;
              if (reportableLimit >= 0 && reportedCount >= reportableLimit) {
                feedDrained = true;
                break;
              }
            }
          }

          if (changesOpts.complete) {
            changesOpts.complete(null, { results, last_seq: lastSeq });
          }
        } catch (e) {
          if (changesOpts.complete) {
            changesOpts.complete(e);
          }
        }
      }

      function setupLiveChanges(liveOpts: any): { cancel: () => void } {
        const id = dbName + ':' + generateUUID();
        let cancelled = false;
        // Whether the CURRENT run of failing polls has already been reported.
        // A live feed polls on every write (notifyChanges) as well as on the
        // 30-second safety net, so reporting per poll would flood Sentry from a
        // single broken device. One report per outage is what a debugger needs:
        // the first failure carries the error, and a successful poll clears the
        // flag so a LATER outage is reported again.
        let pollFailureReported = false;

        function poll() {
          if (cancelled || closed) return;
          const pollOpts: any = {
            since: liveOpts.since,
            include_docs: liveOpts.include_docs,
            conflicts: liveOpts.conflicts,
            style: liveOpts.style,
            doc_ids: liveOpts.doc_ids,
            // #4798: carry the caller's filter into every poll, or a live feed opened
            // with a filter or a selector would report everything.
            filter: liveOpts.filter,
            query_params: liveOpts.query_params,
            return_docs: false,
            onChange(change: any) {
              if (!cancelled && change.seq > liveOpts.since) {
                liveOpts.since = change.seq;
                if (liveOpts.onChange) {
                  liveOpts.onChange(change);
                }
              }
            },
            complete(err: any, _res: any) {
              // A live poll has no caller to return an error to, so it handles
              // its own errors here: the SQL failure from doChanges's catch, and
              // a caller-supplied filter that threw (#4798). It reports them and
              // deliberately does not stop or surface — why that is the right
              // answer is in design-docs/sync-integrity.md.
              //
              // A poll already awaiting SQL when the database is closed or
              // destroyed fails against a database that is going away, which is
              // not a fault worth reporting. _close/_destroy set `closed` (and
              // the listener's own flag), while the handle returned below sets
              // `cancelled`, so both have to be checked.
              if (cancelled || closed) return;
              if (!err) {
                pollFailureReported = false;
                return;
              }
              if (pollFailureReported) return;
              pollFailureReported = true;
              reportAdapterFault('live-changes-poll-failed', {
                error: describeError(err),
                // whether the feed was reading with a filter, which is the
                // difference between "the database read failed" and "the
                // caller's filter threw"
                hasFilter: typeof liveOpts.filter === 'function',
              });
            }
          };
          void doChanges(pollOpts);
        }

        // event-driven: every write calls notifyChanges() which fires poll()
        // immediately. The interval is only a slow safety net against a missed
        // notify — not the primary mechanism. (Previously 500ms, which made
        // every live feed busy-poll by_seq_store ~2x/sec forever, saturating the
        // single SQLite connection even when fully synced and idle.)
        const interval = setInterval(poll, 30000);
        poll();

        const listener = { interval, cancelled: false, poll };
        changesListeners.set(id, listener);

        return {
          cancel() {
            cancelled = true;
            listener.cancelled = true;
            clearInterval(interval);
            changesListeners.delete(id);
          }
        };
      }

      function notifyChanges() {
        // trigger an immediate poll on all live changes listeners so feeds react
        // to writes without waiting for the safety-net interval.
        // called from _bulkDocs inside its try block after cb() — a synchronous
        // throw here would re-enter that catch and double-fire cb()/exitUserWrite(),
        // so isolate each listener's poll().
        for (const [, listener] of changesListeners) {
          if (!listener.cancelled) {
            try {
              listener.poll();
            } catch (err) {
              // poll() only schedules an async doChanges, so a synchronous throw
              // here is unexpected — but it must not propagate into the caller's
              // write-completion path (would double-fire cb()/exitUserWrite()).
              // report it instead, once per adapter: this runs on every write, so
              // a listener that always throws would otherwise flood Sentry (#5191)
              if (!notifyPollThrowReported) {
                notifyPollThrowReported = true;
                reportAdapterFault('notify-changes-poll-threw', { error: describeError(err) });
              }
            }
          }
        }
      }

      // ─── api._getRevisionTree ─────────────────────────────────
      api._getRevisionTree = function (docId: string, cb: (err: any, tree?: any) => void) {
        void (async () => {
          try {
            const res = await sqlite.execute({
              name: dbName,
              sql: `SELECT * FROM doc_store WHERE id = ?`,
              params: [sqlEncode(docId)]
            });
            if (res.rows.length === 0) {
              return cb(MISSING_DOC());
            }
            const metadata = decodeMetadata(res.rows[0]);
            cb(null, metadata.rev_tree);
          } catch (e) {
            cb(e);
          }
        })();
      };

      // ─── api._doCompaction ────────────────────────────────────
      api._doCompaction = function (docId: string, revs: string[], cb: (err?: any) => void) {
        void (async () => {
          try {
            // #4417: this is the same read-modify-write of `doc_store` across
            // separate native calls that `_bulkDocs` runs, so it takes the same
            // serializer. Without it, a compaction reading the rev-tree, then
            // deleting bodies, then writing `doc_store` back could interleave
            // with a `_bulkDocs` write unit and leave a winning revision whose
            // body is gone. See WriteSerializer above.
            await writeSerializer.runExclusiveAsync(async () => {
              // update rev_tree to mark revs as missing
              const metaRes = await sqlite.execute({
                name: dbName,
                sql: `SELECT * FROM doc_store WHERE id = ?`,
                params: [sqlEncode(docId)]
              });
              if (metaRes.rows.length === 0) return;
              const metadata = decodeMetadata(metaRes.rows[0]);

              traverseRevTree(metadata.rev_tree, function (_isLeaf, pos, revHash, _ctx, treeOpts) {
                const rev = pos + '-' + revHash;
                if (revs.indexOf(rev) !== -1) {
                  treeOpts.status = 'missing';
                }
              });

              // delete old sequences
              const compactStmts = revs.map(rev => ({
                sql: `DELETE FROM by_seq_store WHERE doc_id_rev = ?`,
                params: [sqlEncode(docId + '::' + rev)]
              }));
              if (compactStmts.length) {
                await sqlite.executeBatch({ name: dbName, statements: compactStmts });
              }

              // save updated metadata
              const encoded = encodeMetadata(metadata, metadata.winningRev, metadata.deleted);
              await sqlite.executeBatch({
                name: dbName,
                statements: [{
                  sql: `INSERT OR REPLACE INTO doc_store (id, json, winningRev, deleted, seq) VALUES (?, ?, ?, ?, ?)`,
                  params: [encoded.id, encoded.json, encoded.winningRev, encoded.deleted, encoded.seq]
                }]
              });
            });

            cb();
          } catch (e) {
            cb(e);
          }
        })();
      };

      // ─── api._getLocal ────────────────────────────────────────
      api._getLocal = function (id: string, cb: (err: any, doc?: any) => void) {
        void (async () => {
          try {
            const res = await sqlite.execute({
              name: dbName,
              sql: `SELECT * FROM local_store WHERE id = ?`,
              params: [sqlEncode(id)]
            });
            if (res.rows.length === 0) {
              return cb(MISSING_DOC());
            }
            const row = res.rows[0];
            const doc = JSON.parse(row.json);
            doc._id = sqlDecode(row.id);
            doc._rev = row.rev;
            cb(null, doc);
          } catch (e) {
            cb(e);
          }
        })();
      };

      // ─── api._putLocal / api._removeLocal ─────────────────────────
      //
      // #4717: both go through the same planner the batch path uses, so a
      // single write costs ONE native call instead of a read plus a write, and
      // the two paths cannot drift apart in what they store or what they refuse.

      /** Run one planned local write; resolve with its result or throw its rejection. */
      async function runOneLocalWriteAsync(plan: LocalWritePlan): Promise<any> {
        const results: any[] = [];
        await applyLocalWritesAsync(sqlite, dbName, [plan], results);
        const result = results[0];
        // the planner reports a refusal as the value for that slot; the
        // single-document API contract is to throw it instead
        if (!result || result.ok !== true) throw result;
        return result;
      }

      api._putLocal = function (doc: any, putOpts: any, cb: (err: any, result?: any) => void) {
        if (typeof putOpts === 'function') {
          cb = putOpts;
          putOpts = {};
        }
        void (async () => {
          try {
            cb(null, await runOneLocalWriteAsync(planLocalWrite(doc, 0, LocalWriteIntent.Write)));
          } catch (e) {
            cb(e);
          }
        })();
      };

      api._removeLocal = function (doc: any, removeOpts: any, cb: (err: any, result?: any) => void) {
        if (typeof removeOpts === 'function') {
          cb = removeOpts;
          removeOpts = {};
        }
        void (async () => {
          try {
            cb(null, await runOneLocalWriteAsync(planLocalWrite(doc, 0, LocalWriteIntent.Delete)));
          } catch (e) {
            cb(e);
          }
        })();
      };

      // ─── api._close ───────────────────────────────────────────
      api._close = function (cb: (err?: any) => void) {
        void (async () => {
          try {
            closed = true;
            // cancel all live changes
            for (const [, listener] of changesListeners) {
              listener.cancelled = true;
              clearInterval(listener.interval);
            }
            changesListeners.clear();
            await sqlite.close({ name: dbName });
            cb();
          } catch (e) {
            cb(e);
          }
        })();
      };

      // ─── api._destroy ─────────────────────────────────────────
      api._destroy = function (_destroyOpts: any, cb: (err: any, result?: any) => void) {
        void (async () => {
          try {
            closed = true;
            for (const [, listener] of changesListeners) {
              listener.cancelled = true;
              clearInterval(listener.interval);
            }
            changesListeners.clear();
            await sqlite.deleteDatabase({ name: dbName });
            cb(null, { ok: true });
          } catch (e) {
            cb(e);
          }
        })();
      };

      console.log(`PouchDB SQLite adapter: initialized database '${dbName}'`);
      callback(null, api);
    } catch (e) {
      callback(e);
    }
  })();
}

// adapter metadata
(CapacitorSQLitePouch as any).valid = () => true;
